import styled from 'styled-components'
import { useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { t } from 'ttag'

import { Link } from '@h2/common/components/link'
import navianceElementarySvg from '@h2/assets/svg/branding/navianceForElementary.svg'
import headed2Logo from '@h2/assets/svg/branding/headed2Logo.svg'
import poweredBySvg from '@main-images/headed2/new/logo/poweredby.svg'
import poweredBySvgCa from '@main-images/california/new/logo/poweredby.svg'
import headed2AvatarSvg from '@main-images/assets/headed2/avatar/dark.svg'
import headed2NameSvg from '@main-images/assets/headed2/name/name.svg'
import { Headed2LogoIcon } from '@h2/components/icons/headed2Logo'
import { PG, PB60T } from '@h2/components/colors'
import { Box } from '@h2/components/layout/box'
import { Skeleton } from '@h2/components/loading/skeleton'
import { features } from '@h2/components/media'

import { Context } from './context'

import { vagRundFont } from '../../../components/mixins'
import { useNavigator } from '../hooks/useNavigator'
import { useBackgroundImageSize } from '../hooks/useBackgroundImageSize'

const Avatar = styled.span`
  display: block;
  height: 65px;
  width: 65px;
  white-space: nowrap;
  font-size: 0;
  cursor: pointer;
  background-image: url('${({ avatar }) => avatar}');
  background-repeat: no-repeat;
  background-position: center center;

  &:hover {
    transform: rotate(360deg);
    transition: all 1s ease-in-out;
  }
`
const Name = styled.div`
  display: ${({ image }) => (image ? 'block' : 'none')};
  width: ${({ image }) => image?.width * 0.85}px;
  height: ${({ image }) => image?.height * 0.85}px;
  text-indent: -9999px;
  background-image: url('${({ image }) => image?.src}');
  background-repeat: no-repeat;
  background-position: left center;

  @media (${features.minWidth.laptop}) {
    width: ${({ image }) => image?.width}px;
    height: ${({ image }) => image?.height}px;
  }
`
const Header = styled.header`
  div {
    font-size: 26px;
    line-height: 26px;
    letter-spacing: normal;
    margin: 0;
  }

  p {
    margin: 0;
  }
`
const Anchor = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (${features.minWidth.laptop}) {
    justify-content: flex-start;
  }
`
const PoweredBy = styled.p`
  background-image: ${({ tenantId }) =>
    tenantId === 'california' ? `url('${poweredBySvgCa}')` : `url('${poweredBySvg}')`};
  background-repeat: no-repeat;
  background-position: left center;
  text-indent: -9999px;
`
const CustomerName = styled.p`
  ${vagRundFont};
  font-size: 19px;
  color: ${PB60T};
  display: flex;
  align-items: center;
`
const hasClickModifierKey = event =>
  Boolean(event.ctrlKey || event.shiftKey || event.metaKey || event.altKey)
const Branding = ({ renderBranding }) => {
  const ref = useRef()
  const navigator = useNavigator()
  const {
    tenant,
    system,
    tenantId,
    userProfile,
    requireAuthentication,
    customerName,
    subTenantId,
    isNavianceElementary
  } = useContext(Context)
  const [tenantImages, setTenantImages] = useState({ avatar: '', name: '' })
  const [nameRef, image] = useBackgroundImageSize(tenantImages.name)
  const handleBrandingClick = useCallback(
    evt => {
      if (!hasClickModifierKey(evt) && evt.button === 0) {
        evt.preventDefault()
        navigator(evt.currentTarget.getAttribute('href'))
      }
    },
    [navigator]
  )
  const nameImg = useMemo(() => {
    /**
     * Use the aspect ratio of the loaded background image for better
     * cross-browser support. Safari loads SVG's differently than other
     * browsers when no width or height is specified on the img element.
     */

    return {
      ...image,
      width: 350,
      height: image ? Math.round(350 / image.aspectRatio) : 0
    }
  }, [image])

  useEffect(() => {
    let mounted = true
    let avatar = null
    let name = null
    const fetchTenantImages = async () => {
      try {
        let logoPath = tenantId

        switch (tenantId) {
          case 'dreamitva':
            logoPath = 'virginia'
            break
          case 'nola':
            logoPath = 'louisiana'
            break
          case 'wv':
            logoPath = 'west_virginia'
            break
        }

        const [avatarMod, nameMod] = await Promise.all([
          import(
            /* webpackChunkName: "avatar-[request]" */ `@main-images/assets/${logoPath}/avatar/dark.svg`
          ),
          import(
            /* webpackChunkName: "name-[request]" */ `@main-images/assets/${logoPath}/name/name.svg`
          )
        ])

        avatar = avatarMod.default
        name = nameMod.default
      } catch {
        avatar = headed2AvatarSvg
        name = headed2NameSvg
      } finally {
        if (mounted) {
          setTenantImages({ avatar, name })
        }
      }
    }

    fetchTenantImages()

    return () => {
      mounted = false
    }
  }, [tenantId])

  return !isNavianceElementary ? (
    !renderBranding ? (
      <Anchor
        ref={ref}
        href={requireAuthentication && !userProfile ? '/signin' : '/'}
        onClick={handleBrandingClick}>
        <Box margin="0 10px 0 0">
          {tenantImages.avatar ? (
            <Avatar avatar={tenantImages.avatar}>{tenant} Avatar</Avatar>
          ) : (
            <Skeleton circle radius="32.5px" />
          )}
        </Box>
        <Box>
          <Header>
            <Name ref={nameRef} image={nameImg} mark="span" markColor={PG}>
              {tenant} <span>{system}</span>
            </Name>
            {!image && <Skeleton width="300px" height="30px" />}
            {subTenantId ? (
              <CustomerName>
                {t`for`} <Headed2LogoIcon size="small" /> {customerName}
              </CustomerName>
            ) : (
              <PoweredBy tenantId={tenantId}>{t`powered by:`} Headed2</PoweredBy>
            )}
          </Header>
        </Box>
      </Anchor>
    ) : (
      <Link href="/signin">
        <img width="197px" src={headed2Logo} alt="Headed2 Logo" />
      </Link>
    )
  ) : (
    <Link href={requireAuthentication && !userProfile ? '/signin' : '/'}>
      <img width="365px" src={navianceElementarySvg} alt="Naviance Elementary Logo" />
    </Link>
  )
}

export { Branding }
